import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { type ComponentPropsWithRef, type CSSProperties, memo, type ReactNode, useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { cn } from '@air/utils-tailwind';

import { Panel } from '~/components/v2/components/shared/panel/Panel';
import { PanelContent } from '~/components/v2/components/shared/panel/PanelContent';
import { PanelOverlay } from '~/components/v2/components/shared/panel/PanelOverlay';
import {
  getMainSideNavTriggerTransformOrigin,
  MAIN_SIDE_NAV_SHRINK_DURATION_MS,
} from '~/components/v2/components/workspace/main-side-nav/mainSideNavShrinkUtils';
import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';

export type MainSideNavOverlayTrayPlacement = 'top' | 'bottom';

const NESTED_ESCAPE_LAYER_SELECTOR =
  '[data-radix-menu-content],[data-radix-popper-content-wrapper],[role="dialog"],[role="alertdialog"]';

const isNestedEscapeLayerTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest(NESTED_ESCAPE_LAYER_SELECTOR));

export type MainSideNavOverlayTrayProps = {
  children: ReactNode;
  contentId?: string;
  contentRef?: ComponentPropsWithRef<typeof PanelContent>['ref'];
  contentStyle?: CSSProperties;
  contentTestId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  overlayTestId: string;
  placement: MainSideNavOverlayTrayPlacement;
  /** On close, scale down into the rail trigger (`[aria-controls={contentId}]`) instead of sliding left. */
  shrinkIntoTrigger?: boolean;
};

type OverlayTrayEscapeEvent = {
  key: string;
  defaultPrevented: boolean;
  target: EventTarget | null;
  preventDefault: () => void;
  stopPropagation: () => void;
};

export const MainSideNavOverlayTray = memo(
  ({
    children,
    contentId,
    contentRef,
    contentStyle,
    contentTestId,
    onOpenChange,
    open,
    overlayTestId,
    placement,
    shrinkIntoTrigger = false,
  }: MainSideNavOverlayTrayProps) => {
    const internalContentRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const pendingFocusRestoreTargetRef = useRef<HTMLElement | null>(null);
    const setContentRef = useComposedRefs(contentRef, internalContentRef);

    const handleEscape = useCallback(
      (event: OverlayTrayEscapeEvent) => {
        if (event.key !== 'Escape' || event.defaultPrevented || isNestedEscapeLayerTarget(event.target)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        useMainSideNavStore.getState().setOverlayClosedViaEscape(true);
        pendingFocusRestoreTargetRef.current = previouslyFocusedRef.current;
        onOpenChange(false);
      },
      [onOpenChange],
    );

    useIsomorphicLayoutEffect(() => {
      if (!open) {
        return;
      }

      previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      const frame = requestAnimationFrame(() => {
        internalContentRef.current?.focus();
      });

      // Window capture still closes the tray when focus never lands on the panel
      // (rail trigger, overlay). Nested menus/dialogs are skipped via target.
      window.addEventListener('keydown', handleEscape, true);

      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener('keydown', handleEscape, true);

        const focusRestoreTarget = pendingFocusRestoreTargetRef.current;
        pendingFocusRestoreTargetRef.current = null;

        if (!focusRestoreTarget?.isConnected) {
          return;
        }

        // Run after useMainSideNavOverlayExpand's overlay-close timeout so Escape
        // dismiss does not blur the trigger before focus is restored.
        window.setTimeout(() => {
          if (focusRestoreTarget.isConnected) {
            focusRestoreTarget.focus();
          }
        }, 0);
      };
    }, [handleEscape, open]);

    useIsomorphicLayoutEffect(() => {
      const content = internalContentRef.current;

      if (!shrinkIntoTrigger || !contentId || !content) {
        return;
      }

      content.style.transformOrigin = open ? '' : (getMainSideNavTriggerTransformOrigin(content, contentId) ?? '');
      content.style.animationDuration = open ? '' : `${MAIN_SIDE_NAV_SHRINK_DURATION_MS}ms`;
    }, [contentId, open, shrinkIntoTrigger]);

    return (
      <Panel onOpenChange={onOpenChange} open={open}>
        <PanelOverlay
          className="radix-state-closed:animation-duration-300 radix-state-closed:fade-out radix-state-open:animation-duration-300 radix-state-open:fade-in fixed inset-0 z-30 radix-state-closed:animate-out radix-state-open:animate-in bg-transparent"
          // Gallery marquee listens on `#__next`; this tray stays inside that tree.
          data-disableselect="true"
          data-testid={overlayTestId}
        />
        <PanelContent
          // Stay `fixed` (not `absolute`): the layout row is overflow-hidden.
          // Viewport coords match the Strato tray: 10px past the 232px rail, 480×min(70vh,40rem), z-index 200.
          className={cn(
            'radix-state-closed:fade-out radix-state-open:animation-duration-300 radix-state-open:fade-in motion-safe:radix-state-open:slide-in-from-left-12 fixed inset-auto left-[calc(theme(spacing.1)+theme(spacing.58)+10px)] z-[200] flex h-[min(70vh,40rem)] w-[480px] radix-state-open:animate-in flex-col overflow-hidden rounded-16 border ads:border-default border-gray-4 ads:bg-base bg-gray-1 shadow-popover outline-none',
            placement === 'top' ? 'top-20' : 'bottom-1',
            shrinkIntoTrigger
              ? 'motion-safe:radix-state-closed:animation-ease-in motion-safe:radix-state-closed:fade-out motion-safe:radix-state-closed:zoom-out-0 motion-reduce:radix-state-closed:fade-out motion-safe:radix-state-closed:animate-out-late motion-reduce:radix-state-closed:animate-out'
              : 'radix-state-closed:animation-duration-300 motion-safe:radix-state-closed:slide-out-to-left-12 radix-state-closed:animate-out',
          )}
          data-disableselect="true"
          data-testid={contentTestId}
          id={contentId}
          onKeyDown={handleEscape}
          ref={setContentRef}
          style={contentStyle}
          tabIndex={-1}
        >
          <div className="flex h-full min-h-0 flex-col">{children}</div>
        </PanelContent>
      </Panel>
    );
  },
);

MainSideNavOverlayTray.displayName = 'MainSideNavOverlayTray';
